import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Quote, 
  MapPin, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  ArrowRight,
  Star,
  MessageCircle,
  Home,
  Building2,
  ChefHat,
  Bath,
  Share2
} from 'lucide-react';

const CustomerStories = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const stories = [
    {
      id: 1,
      name: 'Sharma Ji',
      location: 'Lucknow, UP',
      type: 'kitchen',
      title: 'Kitchen 2 saal mein kharab hua - Ab CriLam PUR liya',
      hindiTitle: 'शर्मा जी का किचन',
      problem: 'Poora modular kitchen banwaya tha. Carpenter ne water-based glue se laminate paste kiya. 2 saal mein gas stove ke paas laminate uthne laga, sink area mein swelling aa gai.',
      solution: 'Complete kitchen replacement with CriLam PUR-bonded acrylic boards. 10-year guarantee ke saath.',
      result: '3 saal ho gaye, ekdum naya jaisa hai. Gas ke paas bhi koi problem nahi.',
      savings: 'Long-term mein badi bachat - replacement ka kharcha nahi',
      timeline: 'Problem: 2019 | CriLam Solution: 2022 | Status: Perfect in 2025',
      rating: 5,
      quote: '"Pehle carpenter ki sunni, 2 saal mein kitchen barbaad. Ab factory-laminated liya, 10 saal ki tension nahi."',
      beforeImage: '/story-sharma-before.png',
      afterImage: '/story-sharma-after.png'
    },
    {
      id: 2,
      name: 'Gupta Family',
      location: 'Kanpur, UP',
      type: 'bathroom',
      title: 'Bathroom door 6 mahine mein kharab - PUR se permanent solution',
      hindiTitle: 'गुप्ता परिवार',
      problem: 'Bathroom door pe carpenter ne mica laminate paste kiya. 6 mahine mein humidity se edges uthne lage, neeche se board swell ho gaya.',
      solution: 'CriLam PUR-bonded mica doors - specifically designed for wet areas.',
      result: '2 saal se use kar rahe hain, koi swelling nahi, edges perfect hain.',
      savings: 'Door replacement every year vs one-time investment',
      timeline: 'Problem: 2023 | CriLam Solution: 2023 | Status: Perfect',
      rating: 5,
      quote: '"Bathroom mein normal door lagao toh 1 saal bhi nahi chalta. CriLam ka PUR door 2 saal se bilkul naya hai."',
      beforeImage: '/story-gupta-before.png',
      afterImage: '/story-gupta-after.png'
    },
    {
      id: 3,
      name: 'Verma Builders',
      location: 'Delhi NCR',
      type: 'commercial',
      title: 'Builder ne 50 flats mein CriLam use kiya - Zero complaints',
      hindiTitle: 'वर्मा बिल्डर्स',
      problem: 'Pehle local vendors se laminated boards lete the. Handover ke baad customers complaints aati thi - edges lifting, bubbles, etc.',
      solution: 'Complete shift to CriLam PUR-bonded products for all modular work.',
      result: '50+ flats delivered in 2 years. Zero lamination complaints from customers.',
      savings: 'Warranty claims eliminated. Customer satisfaction improved. Repeat business increased.',
      timeline: 'Started: 2023 | Flats Delivered: 50+ | Complaints: Zero',
      rating: 5,
      quote: '"As a builder, my reputation depends on quality. CriLam PUR products eliminated all my lamination-related complaints."',
      beforeImage: '/story-verma-before.png',
      afterImage: '/story-verma-after.png'
    },
    {
      id: 4,
      name: 'Pandey Ji',
      location: 'Varanasi, UP',
      type: 'wardrobe',
      title: 'Wardrobe bubbles aur uneven finish - CriLam se perfect look',
      hindiTitle: 'पांडे जी',
      problem: 'Expensive acrylic laminate lagwaya tha wardrobe pe. Carpenter ne manually paste kiya - jagah jagah bubbles, wavy surface, corners pe gaps.',
      solution: 'New wardrobe with CriLam 6H Acrylic Boards - factory-laminated with PUR.',
      result: 'Mirror-smooth finish, zero bubbles, edges sealed permanently.',
      savings: 'Premium look at standard price - no rework needed',
      timeline: 'Problem: 2024 | CriLam Solution: 2024 | Status: Flawless',
      rating: 5,
      quote: '"Mehenga laminate lagwaya tha, finish cheap si lag rahi thi. CriLam ka board lagaya - ab European furniture jaisa dikhta hai."',
      beforeImage: '/story-pandey-before.png',
      afterImage: '/story-pandey-after.png'
    },
    {
      id: 5,
      name: 'Singh Restaurant',
      location: 'Allahabad, UP',
      type: 'commercial',
      title: 'Restaurant kitchen - Daily use mein bhi perfect after 4 years',
      hindiTitle: 'सिंह रेस्टोरेंट',
      problem: 'Commercial kitchen mein normal laminated boards use kiye the. Heavy daily use, heat, water - sab ek saal mein kharab.',
      solution: 'Complete kitchen upgrade with CriLam PUR-bonded boards - commercial grade.',
      result: '4 saal se daily commercial use - still performing like new.',
      savings: 'Eliminated yearly replacement costs. ROI recovered in 2 years.',
      timeline: 'Problem: 2020 | CriLam Solution: 2021 | Status: 4 Years Perfect',
      rating: 5,
      quote: '"Restaurant kitchen mein normal boards 1 saal bhi nahi chalte the. CriLam ka 4 saal ho gaye - no maintenance needed."',
      beforeImage: '/story-singh-before.png',
      afterImage: '/story-singh-after.png'
    },
    {
      id: 6,
      name: 'Agarwal Ji',
      location: 'Gorakhpur, UP',
      type: 'kitchen',
      title: 'Gas stove ke paas laminate uthta tha - Ab heat-proof solution',
      hindiTitle: 'अग्रवाल जी',
      problem: 'Kitchen mein gas ke paas ka cabinet hamesha kharab hota tha. Har 2 saal mein repair karwana padta tha.',
      solution: 'Replaced with CriLam PUR boards - heat resistant up to 150°C.',
      result: '3 saal se daily cooking - no laminate lifting near gas stove.',
      savings: 'Bi-yearly repair cost eliminated',
      timeline: 'Problem: Recurring since 2018 | CriLam Solution: 2022 | Status: No repairs needed',
      rating: 5,
      quote: '"Gas ke paas hamesha problem aati thi. CriLam lagaya - 3 saal se koi dikkat nahi. Heat-proof actually kaam karta hai."',
      beforeImage: '/story-agarwal-before.png',
      afterImage: '/story-agarwal-after.png'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Stories', icon: Star },
    { id: 'kitchen', label: 'Kitchen', icon: ChefHat },
    { id: 'bathroom', label: 'Bathroom', icon: Bath },
    { id: 'wardrobe', label: 'Wardrobe', icon: Home },
    { id: 'commercial', label: 'Commercial', icon: Building2 }
  ];

  const filteredStories = activeFilter === 'all' 
    ? stories 
    : stories.filter(s => s.type === activeFilter);

  const handleShare = async (story) => {
    const shareText = `${story.name} from ${story.location}: "${story.quote}" - CriLam PUR-Bond™ Technology`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `CriLam Customer Story - ${story.name}`,
          text: shareText,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText + ' ' + window.location.href);
      alert('Story link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="customer-stories-page">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              REAL STORIES
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Customer Stories
            </h1>
            <p className="text-xl text-[#D4AF37] font-medium mb-4">
              Problem-Solution Stories That Customers Connect With
            </p>
            <p className="text-lg text-white/70 max-w-2xl">
              Real experiences from real customers. See how CriLam PUR-Bond™ technology solved their lamination problems permanently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-6 bg-white border-b sticky top-24 z-30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2 justify-center">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all rounded-full ${
                  activeFilter === filter.id
                    ? 'bg-[#004534] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${filter.id}`}
              >
                <filter.icon className="h-4 w-4" />
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="space-y-12">
            {filteredStories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
                data-testid={`story-card-${story.id}`}
              >
                {/* Story Header */}
                <div className="bg-[#004534] text-white p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-[#D4AF37] text-sm mb-2">
                        <MapPin className="h-4 w-4" />
                        {story.location}
                      </div>
                      <h2 className="text-2xl font-bold">{story.name}</h2>
                      <p className="text-white/70 text-sm">{story.hindiTitle}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(story.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Story Content */}
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-bold text-[#004534] mb-6">
                    {story.title}
                  </h3>

                  <div className="grid lg:grid-cols-3 gap-6 mb-8">
                    {/* Problem */}
                    <div className="p-5 bg-red-50 rounded-xl border border-red-200">
                      <div className="flex items-center gap-2 text-red-600 font-semibold mb-3">
                        <XCircle className="h-5 w-5" />
                        Problem (समस्या)
                      </div>
                      <p className="text-gray-700 text-sm">{story.problem}</p>
                    </div>

                    {/* Solution */}
                    <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-center gap-2 text-blue-600 font-semibold mb-3">
                        <CheckCircle className="h-5 w-5" />
                        CriLam Solution
                      </div>
                      <p className="text-gray-700 text-sm">{story.solution}</p>
                    </div>

                    {/* Result */}
                    <div className="p-5 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2 text-green-600 font-semibold mb-3">
                        <CheckCircle className="h-5 w-5" />
                        Result (नतीजा)
                      </div>
                      <p className="text-gray-700 text-sm">{story.result}</p>
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="bg-[#F7F8F5] p-6 rounded-xl mb-6">
                    <div className="flex gap-4">
                      <Quote className="h-8 w-8 text-[#D4AF37] flex-shrink-0" />
                      <div>
                        <p className="text-lg text-[#004534] italic font-medium">
                          {story.quote}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          - {story.name}, {story.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {story.timeline}
                      </div>
                    </div>
                    <button
                      onClick={() => handleShare(story)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#004534] hover:bg-[#004534] hover:text-white rounded-full transition-colors border border-[#004534]"
                    >
                      <Share2 className="h-4 w-4" />
                      Share Story
                    </button>
                  </div>

                  {/* Savings Highlight */}
                  <div className="mt-4 p-4 bg-[#D4AF37]/10 rounded-lg">
                    <p className="text-sm font-medium text-[#004534]">
                      <span className="text-[#D4AF37]">Savings:</span> {story.savings}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Your Story CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <MessageCircle className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-[#004534] mb-4 font-['Outfit']">
              Share Your CriLam Story
            </h2>
            <p className="text-gray-600 mb-8">
              Have you experienced the CriLam PUR difference? Share your story and help others make informed decisions.
            </p>
            <Link to="/contact">
              <button className="inline-flex items-center gap-2 px-8 py-4 bg-[#004534] text-white font-medium hover:bg-[#D4AF37] hover:text-[#004534] transition-colors">
                Share Your Story
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#004534] relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Don&apos;t Become Another Problem Story
            </motion.h2>
            <motion.p 
              className="text-lg text-white/70 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Choose CriLam PUR-bonded products from the start and join our success stories instead.
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/pur-technology">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2">
                  Learn About PUR
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/contact">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Get Started Today
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerStories;
